import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useFundContext from '@/contexts/Fund/useFundContext';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { useGetAuthApi, usePutAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { FundCoordinatorDtoOut } from '@/models/out/FundCoordinatorDtoOut';
import { ContactDtoOut } from '@/models/out/ContactDtoOut';
import { FundClassification } from '@/models/common/FundClassification';
import { FundStatus } from '@/models/common/FundStatus';
import { CreateInquiryDtoOut } from '@/models/out/CreateInquiryDtoOut';
import CopyButton from '@/components/ui/CopyButton/CopyButton';
import Button from '@/components/ui/Button';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import SignatureCanvas, { stripSignatureDataUrl } from '@/components/ui/SignatureCanvas/SignatureCanvas';
import NewInquiryDialog from '@/components/Inquiries/NewInquiryDialog';
import useInquiriesFeature from '@/hooks/useInquiriesFeature';
import { TwoSignatureRequirementDtoOut } from '@/models/out/TwoSignatureRequirementDtoOut';
import { ApproveDisableTwoSignatureRequirementDtoIn } from '@/models/in/ApproveDisableTwoSignatureRequirementDtoIn';
import { getRepresentativeActionErrorMessage } from '@/helpers/apiErrorHelper';
import { formatDateLocale } from '@/helpers/dateHelper';
import externalLinkIcon from '@/assets/icons/external-link.svg';
import PeopleSection from './people/PeopleSection';
import styles from './FundSettings.module.css';

/** The phase-2 flag: the managed representatives, their history and the 2-signature toggle. */
export const FUND_REPRESENTATIVES_FEATURE = 'fund-representatives';

/**
 * An email address and a phone number on one line under a name.
 *
 * Each value gets its own <bdi>: they are left-to-right runs inside a right-to-left line, and
 * joining them into a single string let the browser reorder the whole thing - «7173065@gmail.com •
 * +972527173065» was read on screen as «gmail.com • +972527173065@7173065». Isolating each value
 * lays each one out on its own and keeps the separator between them.
 */
const ContactValues = ({ values }: { values: (string | null | undefined)[] }) => {
  const present = values.filter((value): value is string => !!value);
  if (present.length === 0) return null;

  return (
    <span className={styles.personContact}>
      {present.map((value, index) => (
        <span key={value}>
          {index > 0 && ' • '}
          <bdi dir="ltr">{value}</bdi>
        </span>
      ))}
    </span>
  );
};

/**
 * «לשינוי: פנייה למשרד» - the one way a coordinator changes anything on this screen. Opens the
 * portal's own inquiry dialog with the fund already named, so the office reads "settings of fund
 * 5833" without asking. Rendered only while inquiries are on for this fund, like every other place
 * that opens one (see OpenInquiryButton).
 */
const OfficeInquiryLink = ({ fundId, fundName }: { fundId: number; fundName: string }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const create = usePostAuthApi<{ id: number }, CreateInquiryDtoOut>(`funds/${fundId}/inquiries?_=1`, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`funds/${fundId}/inquiries`] });
      setIsOpen(false);
    },
    onError: () => setErrorMessage(t('inquiries.newInquiryDialog.error')),
  });

  return (
    <>
      <button type="button" className={styles.linkBtn} onClick={() => { setErrorMessage(null); setIsOpen(true); }}>
        <img src={externalLinkIcon} alt="" className={styles.actionIcon} />
        {t('fundSettingsInfoPage.contactOffice')}
      </button>
      <NewInquiryDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={(dto) => { setErrorMessage(null); create.mutate(dto); }}
        resourceType="general"
        resourceId={null}
        resourceSummary={[{ label: t('fundSettingsInfoPage.inquiryRegarding'), value: t('fundSettingsInfoPage.inquirySubject', { fundId, fundName }) }]}
        isSubmitting={create.isPending}
        errorMessage={errorMessage}
      />
    </>
  );
};

/**
 * SPR-1054: fund-representative self-service toggle for requiring 2 representative signatures
 * before a support request is sent to the office. Enabling is a single-actor action; disabling
 * once enabled requires 2 different representatives to sign a disable approval, so the lock
 * can't be bypassed unilaterally. Phase 2 (HAT-1231): shown behind the representatives flag.
 */
const TwoSignatureRequirementSection = ({ fundId, onlyWhenEnabled = false }: { fundId: number; onlyWhenEnabled?: boolean }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settingUrl = `funds/financial-data/${fundId}/two-signature-requirement`;
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);
  const [signature, setSignature] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: status } = useGetAuthApi<TwoSignatureRequirementDtoOut>(settingUrl);

  const setEnabled = usePutAuthApi<TwoSignatureRequirementDtoOut, object, { enabled: boolean }>(settingUrl, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [settingUrl] });
      // The toggle is one of the changes the history records - refresh it too.
      queryClient.invalidateQueries({ queryKey: [`funds/financial-data/${fundId}/settings-history`] });
    },
  });

  const approveDisable = usePostAuthApi<TwoSignatureRequirementDtoOut, ApproveDisableTwoSignatureRequirementDtoIn>(
    `${settingUrl}/disable-approvals`
  );

  const openDisableDialog = () => {
    setSignature('');
    setError(null);
    setIsDisableDialogOpen(true);
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setEnabled.mutate({ params: {}, body: { enabled: true } });
    } else {
      openDisableDialog();
    }
  };

  const handleApproveDisable = () => {
    if (!signature) return;
    setError(null);

    const body: ApproveDisableTwoSignatureRequirementDtoIn = {
      signature: {
        name: 'signature.png',
        description: 'representative approval to disable 2-signature requirement',
        contentBase64: stripSignatureDataUrl(signature),
      },
    };

    approveDisable.mutate(body, {
      onSuccess: (updatedStatus) => {
        queryClient.invalidateQueries({ queryKey: [settingUrl] });
        queryClient.invalidateQueries({ queryKey: [`funds/financial-data/${fundId}/settings-history`] });
        if (!updatedStatus.enabled) {
          setIsDisableDialogOpen(false);
        } else {
          setSignature('');
        }
      },
      onError: (err) => {
        setError(getRepresentativeActionErrorMessage(err, t, 'fundSettingsInfoPage.twoSignatureRequirementDisableError'));
      },
    });
  };

  // Why the switch is not for this caller right now, in one sentence (review, notes 30-32).
  const signers = status?.signerCount ?? 0;
  const minimum = status?.minimumSigners ?? 2;
  // Turning it on, or starting to turn it off, is the lead coordinator's where «רכזים והרשאות» is on.
  const needsLead = !!status?.leadCoordinatorsOnly && (!status.enabled || (status.pendingDisableApprovals?.length ?? 0) === 0);
  const blockedReason = !status
    ? null
    : !status.callerCanChange
      ? t(needsLead ? 'fundSettingsInfoPage.twoSignatureOnlyLead' : 'fundSettingsInfoPage.twoSignatureOnlySigners')
      : !status.enabled && signers < minimum
        ? t('fundSettingsInfoPage.twoSignatureNeedSigners', { min: minimum, count: signers })
        : null;
  const pendingApprovals = status?.pendingDisableApprovals ?? [];

  if (onlyWhenEnabled && !status?.enabled) return null;

  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>{t('fundSettingsInfoPage.twoSignatureRequirementTitle')}</h2>
      <ToggleSwitch
        label={t('fundSettingsInfoPage.twoSignatureRequirementToggleLabel')}
        checked={status?.enabled ?? false}
        onChange={handleToggle}
        disabled={setEnabled.isPending || !status || !!blockedReason}
      />
      <p className={styles.panelSubtitle}>
        {t('fundSettingsInfoPage.twoSignatureRequirementExplanation')}
        {blockedReason && <> <b>{blockedReason}</b></>}
      </p>
      {status?.enabled && pendingApprovals.length > 0 && (
        <p className={styles.pendingNote}>
          {t('fundSettingsInfoPage.twoSignatureDisablePending', {
            name: pendingApprovals[pendingApprovals.length - 1].contactName,
            date: formatDateLocale(pendingApprovals[pendingApprovals.length - 1].signedAt),
          })}
          {status.callerCanChange && (
            <button type="button" className={styles.linkBtn} onClick={openDisableDialog}>
              {t('fundSettingsInfoPage.twoSignatureApproveDisable')}
            </button>
          )}
        </p>
      )}
      {setEnabled.isError && (
        <p className={styles.formError}>
          {getRepresentativeActionErrorMessage(setEnabled.error, t, 'fundSettingsInfoPage.twoSignatureRequirementEnableError')}
        </p>
      )}

      <MainDialogLayout
        open={isDisableDialogOpen}
        onOpenChange={setIsDisableDialogOpen}
        title={t('fundSettingsInfoPage.twoSignatureRequirementDisableTitle')}
      >
        <div className={styles.bankForm}>
          <p className={styles.panelSubtitle}>{t('fundSettingsInfoPage.twoSignatureRequirementDisableExplanation')}</p>

          {status && status.pendingDisableApprovals.length > 0 && (
            <p className={styles.panelSubtitle}>
              {t('fundSettingsInfoPage.twoSignatureRequirementPendingApprovals', {
                count: status.pendingDisableApprovals.length,
                required: status.requiredApprovalsToDisable,
              })}
            </p>
          )}

          <SignatureCanvas onCapture={setSignature} invalid={!!error && !signature} />

          {error && <p className={styles.formError}>{error}</p>}

          <div className={styles.formActions}>
            <Button variant="primary" onClick={handleApproveDisable} disabled={approveDisable.isPending || !signature}>
              {approveDisable.isPending ? t('loading') : t('fundSettingsInfoPage.twoSignatureRequirementDisableConfirm')}
            </Button>
            <Button variant="outline" onClick={() => setIsDisableDialogOpen(false)} disabled={approveDisable.isPending}>
              {t('fundSettingsInfoPage.twoSignatureRequirementDisableCancel')}
            </Button>
          </div>
        </div>
      </MainDialogLayout>
    </div>
  );
};

/**
 * «הגדרות הקרן» (HAT-1231).
 *
 * Phase 1, for every coordinator: what the fund is (name, number, classification, status, the
 * supported person of a welfare fund) and who its coordinators are - read-only, with one way to
 * ask for a change: an inquiry to the office. Phase 2, behind the `fund-representatives` flag:
 * the coordinators panel becomes «אנשי הקרן» - the same people with their roles and permissions,
 * managed by the fund's founders, with the change history at its foot - plus the 2-signature
 * toggle. Both wait for the office to run «מינוי מייסדים» first.
 *
 * What the coordinator hands to donors and to an accountant is not here - it lives on
 * «אישורים ומסמכים», its own item in the menu.
 */
const FundSettingsPage = () => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const { currentUser } = useAuthContext();
  const isRepresentativesOn = useFeature(FUND_REPRESENTATIVES_FEATURE);
  const isInquiriesOn = useInquiriesFeature();
  const isAdmin = !!currentUser?.roles?.includes('Admin');

  const { data: coordinators, isLoading: isCoordinatorsLoading } = useGetAuthApi<FundCoordinatorDtoOut[]>(
    currentFund ? `funds/financial-data/${currentFund.id}/coordinators` : ''
  );
  const isWelfareFund = currentFund?.classification === FundClassification.Welfare;
  const { data: supporteds } = useGetAuthApi<ContactDtoOut[]>(
    currentFund && isWelfareFund ? `funds/${currentFund.id}/supporteds/basic` : ''
  );

  if (!currentFund) return <h1>{t('noFundSelected')}.</h1>;

  const classificationLabel = currentFund.classification
    ? t(`featureFlags.classificationLabels.${currentFund.classification}`, { defaultValue: currentFund.classification })
    : null;
  const statusLabel = currentFund.status
    ? t(`fundSettingsInfoPage.status.${currentFund.status}`, { defaultValue: currentFund.status })
    : null;
  const supportedName = supporteds?.[0] ? `${supporteds[0].firstName} ${supporteds[0].lastName}`.trim() : null;

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>{t('fundSettingsInfoPage.title')}</h1>

      <div className={styles.panel}>
        <h2 className={styles.panelTitle}>{t('fundSettingsInfoPage.fundDetailsTitle')}</h2>
        <dl className={styles.factRow}>
          <div className={styles.fact}>
            <dt className={styles.statLabel}>{t('fundSettingsInfoPage.fundName')}</dt>
            <dd className={styles.factValue}>{currentFund.name}</dd>
          </div>
          <div className={styles.fact}>
            <dt className={styles.statLabel}>{t('fundSettingsInfoPage.fundNumber')}</dt>
            <dd className={styles.factValue}>
              <span>{currentFund.id}</span>
              <CopyButton value={String(currentFund.id)} />
            </dd>
          </div>
          {classificationLabel && (
            <div className={styles.fact}>
              <dt className={styles.statLabel}>{t('fundSettingsInfoPage.classification')}</dt>
              <dd className={styles.factValue}>{classificationLabel}</dd>
            </div>
          )}
          {statusLabel && (
            <div className={styles.fact}>
              <dt className={styles.statLabel}>{t('fundSettingsInfoPage.statusLabel')}</dt>
              <dd className={`${styles.factValue} ${currentFund.status === FundStatus.Active ? '' : styles.factValueAttention}`.trim()}>
                {statusLabel}
              </dd>
            </div>
          )}
          {isWelfareFund && supportedName && (
            <div className={styles.fact}>
              <dt className={styles.statLabel}>{t('fundSettingsInfoPage.supported')}</dt>
              <dd className={styles.factValue}>
                <Link to={`/${currentFund.id}/support/supporteds`} className={styles.factLink}>{supportedName}</Link>
              </dd>
            </div>
          )}
        </dl>
        {/* With inquiries off for this fund there is no link to hang the sentence on, so it ends itself. */}
        <p className={`${styles.panelSubtitle} ${styles.inlineNote}`}>
          {isRepresentativesOn
            ? t(isInquiriesOn ? 'fundSettingsInfoPage.detailsSetByOfficeNoPeople' : 'fundSettingsInfoPage.detailsSetByOfficeNoPeopleNoInquiries')
            : t(isInquiriesOn ? 'fundSettingsInfoPage.detailsSetByOffice' : 'fundSettingsInfoPage.detailsSetByOfficeNoInquiries')}
          {isInquiriesOn && <OfficeInquiryLink fundId={currentFund.id} fundName={currentFund.name} />}
        </p>
      </div>

      {isRepresentativesOn ? (
        <PeopleSection fundId={currentFund.id} fundName={currentFund.name} />
      ) : (
      <div className={styles.panel}>
        <h2 className={styles.panelTitle}>{t('fundSettingsInfoPage.coordinators')}</h2>
        {isCoordinatorsLoading ? (
          <p className={styles.panelSubtitle}>{t('loading')}</p>
        ) : coordinators && coordinators.length > 0 ? (
          <div className={styles.personList}>
            {coordinators.map((coordinator) => {
              const isMe = !!currentUser?.email && coordinator.email?.toLowerCase() === currentUser.email.toLowerCase();
              return (
                <div className={styles.personRow} key={coordinator.email}>
                  <span className={styles.personName}>
                    {coordinator.firstName} {coordinator.lastName}
                    {isMe && <span className={styles.mePill}>{t('fundSettingsInfoPage.you')}</span>}
                  </span>
                  <ContactValues values={[coordinator.email, coordinator.phone]} />
                </div>
              );
            })}
          </div>
        ) : (
          // A coordinator looking at this screen is one of the fund's coordinators, so the empty
          // state is only ever seen by an administrator opening someone else's fund.
          <p className={styles.panelSubtitle}>{isAdmin ? t('fundSettingsInfoPage.noCoordinatorsAdmin') : t('fundSettingsInfoPage.noCoordinators')}</p>
        )}
        <p className={styles.panelSubtitle}>{t(isInquiriesOn ? 'fundSettingsInfoPage.coordinatorsManagedByOffice' : 'fundSettingsInfoPage.coordinatorsManagedByOfficeNoInquiries')}</p>
      </div>
      )}

      {/* With the flag off, still shown while the requirement is on: a fund that turned it on is held
          to it on the server, and must be able to see that and turn it off (15.10.2026). */}
      <TwoSignatureRequirementSection fundId={currentFund.id} onlyWhenEnabled={!isRepresentativesOn} />
    </div>
  );
};

export default FundSettingsPage;
